/**
 * @module hooks
 * @description Tournament WebSocket Hook — 連接 /tournament namespace，接收即時計時器更新
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '../store/auth.store';
import { employeeSocketSession } from '../lib/employeeSocketSession';
import type {
  WsTimerUpdate,
  WsPlayerEliminated,
  WsPrizePoolUpdated,
  WsTournamentStatusChanged,
  TimerActionDto,
  TournamentStatus,
} from '@pokerroom/types';

const WS_URL = (import.meta as unknown as { env: { VITE_WS_URL: string } }).env.VITE_WS_URL ?? '';

interface TournamentSocketState {
  timerUpdate: WsTimerUpdate | null;
  lastElimination: WsPlayerEliminated | null;
  prizePoolUpdate: WsPrizePoolUpdated | null;
  tournamentStatus: TournamentStatus | null;
  connected: boolean;
  sessionPaused: boolean;
  socketError: string;
}

interface UseTournamentSocketReturn extends TournamentSocketState {
  clearSocketError: () => void;
  retrySocket: () => void;
  sendTimerAction: (action: TimerActionDto) => void;
  updateTimerSnapshot: (update: WsTimerUpdate, expected: WsTimerUpdate | null) => void;
}

/**
 * 連接 /tournament namespace 並訂閱即時事件
 *
 * @param tournamentId - 要訂閱的賽事 ID
 * @param token - JWT 存取 Token（用於 WsJwtGuard）
 * @param enabled - 是否啟用連線（預設 true）
 */
export function useTournamentSocket(
  tournamentId: string | undefined,
  token: string | undefined,
  enabled = true,
): UseTournamentSocketReturn {
  const revision = useAuthStore((state) => state.sessionRevision);
  const contextRef = useRef<{ tournamentId: string; token: string; revision: number } | null>(null);
  const sessionRef = useRef<ReturnType<typeof employeeSocketSession> | null>(null);
  const socketRef = useRef<Socket | null>(null);

  const [state, setState] = useState<TournamentSocketState>({
    timerUpdate: null,
    lastElimination: null,
    prizePoolUpdate: null,
    tournamentStatus: null,
    connected: false,
    sessionPaused: false,
    socketError: '',
  });

  const updateTimerSnapshot = useCallback(
    (timerUpdate: WsTimerUpdate, expected: WsTimerUpdate | null) => {
      if (
        contextRef.current?.tournamentId !== tournamentId ||
        contextRef.current?.token !== token ||
        contextRef.current?.revision !== revision ||
        !sessionRef.current?.canAct()
      )
        return;
      // Do not replace a newer socket snapshot with a delayed HTTP acknowledgement.
      setState((previous) =>
        previous.timerUpdate === expected ? { ...previous, timerUpdate } : previous,
      );
    },
    [tournamentId, token, revision],
  );

  useEffect(() => {
    if (!enabled || !tournamentId || !token) return;

    setState((prev) => ({ ...prev, connected: false, socketError: '' }));
    const socket = io(`${WS_URL}/tournament`, {
      withCredentials: true,
      auth: { token },
      reconnection: true,
      reconnectionDelay: 100,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: Infinity,
    });

    socketRef.current = socket;
    contextRef.current = { tournamentId, token, revision };
    const session = employeeSocketSession(socket, token, (sessionPaused) => {
      setState((prev) => ({
        ...prev,
        sessionPaused,
        connected: false,
        timerUpdate: null,
        lastElimination: null,
        prizePoolUpdate: null,
        tournamentStatus: null,
      }));
    });
    sessionRef.current = session;
    setState((prev) => ({ ...prev, sessionPaused: false }));

    socket.on('connect', () => {
      if (!session.canAct()) return;
      setState((prev) => ({ ...prev, connected: true }));
      socket.emit('tournament:join', { tournamentId });
    });

    socket.on('disconnect', () => {
      if (!session.current()) return;
      setState((prev) => ({ ...prev, connected: false }));
    });

    socket.on('connect_error', (err) => {
      if (!session.canAct()) return;
      console.warn('[WS] tournament connect_error:', err.message);
      setState((prev) => ({ ...prev, connected: false, socketError: err.message }));
    });

    socket.on('exception', (error: { message?: string } | string) => {
      if (!session.canAct()) return;
      const message = typeof error === 'string' ? error : error?.message;
      setState((prev) => ({ ...prev, socketError: message || 'Timer action failed' }));
    });

    socket.on('tournament:timer:update', (data: WsTimerUpdate) => {
      if (!session.canAct()) return;
      setState((prev) => ({ ...prev, timerUpdate: data }));
    });

    socket.on('tournament:player:eliminated', (data: WsPlayerEliminated) => {
      if (!session.canAct()) return;
      setState((prev) => ({ ...prev, lastElimination: data }));
    });

    socket.on('tournament:prize_pool:updated', (data: WsPrizePoolUpdated) => {
      if (!session.canAct()) return;
      setState((prev) => ({ ...prev, prizePoolUpdate: data }));
    });

    socket.on('tournament:status:changed', (data: WsTournamentStatusChanged) => {
      if (!session.canAct()) return;
      setState((prev) => ({ ...prev, tournamentStatus: data.status }));
    });

    return () => {
      if (session.canAct()) socket.emit('tournament:leave', { tournamentId });
      session.dispose();
      if (socketRef.current === socket) {
        socketRef.current = null;
        sessionRef.current = null;
      }
    };
  }, [tournamentId, token, enabled, revision]);

  const sendTimerAction = useCallback(
    (action: TimerActionDto) => {
      if (
        contextRef.current?.tournamentId !== tournamentId ||
        contextRef.current?.token !== token ||
        contextRef.current?.revision !== revision ||
        !sessionRef.current?.canAct() ||
        !socketRef.current?.connected
      ) {
        setState((prev) => ({ ...prev, socketError: 'Socket disconnected' }));
        return;
      }
      setState((prev) => ({ ...prev, socketError: '' }));
      socketRef.current.emit('tournament:timer:action', {
        tournamentId,
        action,
      });
    },
    [tournamentId, token, revision],
  );

  const clearSocketError = useCallback(() => {
    setState((prev) => ({ ...prev, socketError: '' }));
  }, []);

  const retrySocket = useCallback(() => {
    if (
      contextRef.current?.tournamentId !== tournamentId ||
      contextRef.current?.token !== token ||
      contextRef.current?.revision !== revision
    )
      return;
    sessionRef.current?.retry();
  }, [tournamentId, token, revision]);

  return { ...state, retrySocket, sendTimerAction, updateTimerSnapshot, clearSocketError };
}
